import { pdfDefaultOptions } from '../options/pdf-default-options';

export class PositioningService {
  private static readonly DOORHANGER_OFFSET = 17;
  private static readonly TOOLBAR_MARGIN = 4;

  // Button → popup pairs for all positionable popups
  private static readonly POPUP_PAIRS: Array<[string, string]> = [
    ['primaryViewFind', 'findbar'],
    ['secondaryToolbarToggle', 'secondaryToolbar'],
    ['primaryEditorFreeText', 'editorFreeTextParamsToolbar'],
    ['primaryEditorInk', 'editorInkParamsToolbar'],
    ['primaryEditorEraser', 'editorEraserParamsToolbar'], // stephanrauh/pdf.js#14 - the eraser
    ['primaryEditorHighlight', 'editorHighlightParamsToolbar'],
    ['primaryEditorStamp', 'editorStampParamsToolbar'],
    ['editorCommentButton', 'editorCommentParamsToolbar'],
    ['primaryEditorSignatureButton', 'editorSignatureParamsToolbar'],
    // #3257 modified by ngx-extended-pdf-viewer
    ['signaturePropertiesButton', 'signaturePropertiesPanel'],
    // #3257 end of modification by ngx-extended-pdf-viewer
  ];

  public positionPopupBelowItsButton(buttonId: string, popupId: string): void {
    if (!pdfDefaultOptions.positionPopupDialogsWithJavaScript) {
      return;
    }

    setTimeout(() => {
      const button = this.findVisibleButton(buttonId);
      const popup = this.getPopupElement(popupId);

      if (!button || !popup) return;

      this.applyPopupPositioning(button, popup);
    });
  }

  /**
   * Re-positions all currently open/visible popups. Call this when the
   * toolbar layout changes (e.g., mobileFriendlyZoom changes at runtime).
   */
  public repositionOpenPopups(): void {
    if (!pdfDefaultOptions.positionPopupDialogsWithJavaScript) {
      return;
    }

    setTimeout(() => {
      for (const [buttonId, popupId] of PositioningService.POPUP_PAIRS) {
        const popup = document.querySelector<HTMLElement>(`#${popupId}`);
        if (!popup) continue;

        // A popup is "open" if it's visible (no hidden class/attribute and has offsetParent)
        const isVisible = !popup.classList.contains('hidden') && !popup.hasAttribute('hidden') && popup.offsetParent !== null;
        if (!isVisible) continue;

        const button = this.findVisibleButton(buttonId);
        if (!button) continue;

        this.applyPopupPositioning(button, popup);
      }
    });
  }

  private findVisibleButton(buttonId: string): HTMLElement | null {
    const findVisible = (selector: string) => Array.from(document.querySelectorAll<HTMLElement>(`#${selector}`)).find((el) => el.offsetParent !== null);

    return findVisible(buttonId) || findVisible('secondaryToolbarToggle') || null;
  }

  private getPopupElement(popupId: string): HTMLElement | null {
    const popup = document.querySelector<HTMLElement>(`#${popupId}`);
    return popup?.offsetParent ? popup : null;
  }

  private applyPopupPositioning(button: HTMLElement, popup: HTMLElement): void {
    const popupContainer = popup.offsetParent as HTMLElement;
    const buttonRect = button.getBoundingClientRect();
    const containerRect = popupContainer.getBoundingClientRect();

    // #3253 modified by ngx-extended-pdf-viewer
    // The reading direction is set on the viewer's container, not on <html>,
    // so ask the nearest ancestor carrying a dir attribute.
    const isRTL = popup.closest('[dir]')?.getAttribute('dir') === 'rtl';
    // #3253 end of modification by ngx-extended-pdf-viewer
    let isRightAligned = isRTL;
    if (popup.classList.contains('doorHangerRight')) {
      isRightAligned = !isRTL;
    }

    this.setBasicPopupStyles(popup, isRightAligned);
    this.setHorizontalPosition(popup, buttonRect, containerRect, isRightAligned);
    this.setVerticalPosition(popup, containerRect);
  }

  private setBasicPopupStyles(popup: HTMLElement, isRightAligned: boolean): void {
    Object.assign(popup.style, {
      position: 'absolute',
      display: 'block',
      transformOrigin: isRightAligned ? 'top right' : 'top left',
    });
  }

  private setHorizontalPosition(popup: HTMLElement, buttonRect: DOMRect, containerRect: DOMRect, isRightAligned: boolean): void {
    const centerOffset = buttonRect.width / 2 - PositioningService.DOORHANGER_OFFSET;

    if (isRightAligned) {
      popup.style.right = `${containerRect.right - buttonRect.right + centerOffset}px`;
      popup.style.left = '';
    } else {
      popup.style.left = `${buttonRect.left - containerRect.left + centerOffset}px`;
      popup.style.right = '';
    }
  }

  private setVerticalPosition(popup: HTMLElement, containerRect: DOMRect): void {
    const toolbarContainer = document.querySelector<HTMLElement>('#toolbarContainer');
    if (!toolbarContainer) return;

    const toolbarRect = toolbarContainer.getBoundingClientRect();
    const top = toolbarRect.bottom - containerRect.top + PositioningService.TOOLBAR_MARGIN;
    popup.style.top = `${top}px`;
  }
}
